import isEqual from "lodash/isEqual";
import { CustomShapeSource } from "../mapbox/hooks/style-hooks";
import some from "lodash/some";
import isEmpty from "lodash/isEmpty";
import { vertexLayers } from "./style-utils";
import isString from "lodash/isString";

//  ██████╗ ███████╗███╗   ██╗███████╗██████╗  █████╗ ██╗
// ██╔════╝ ██╔════╝████╗  ██║██╔════╝██╔══██╗██╔══██╗██║
// ██║  ███╗█████╗  ██╔██╗ ██║█████╗  ██████╔╝███████║██║
// ██║   ██║██╔══╝  ██║╚██╗██║██╔══╝  ██╔══██╗██╔══██║██║
// ╚██████╔╝███████╗██║ ╚████║███████╗██║  ██║██║  ██║███████╗
//  ╚═════╝ ╚══════╝╚═╝  ╚═══╝╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝

export const isTouchEvent = (
    value: (mapboxgl.MapMouseEvent & mapboxgl.EventData) | TouchEvent
): value is TouchEvent => {
    return value.type.includes("touch");
};

export const isMapTouchEvent = (
    value:
        | (mapboxgl.MapMouseEvent & mapboxgl.EventData)
        | (mapboxgl.MapTouchEvent & mapboxgl.EventData)
): value is mapboxgl.MapTouchEvent & mapboxgl.EventData => {
    return value.type.includes("touch");
};

// ███████╗██╗   ██╗███████╗███╗   ██╗████████╗███████╗
// ██╔════╝██║   ██║██╔════╝████╗  ██║╚══██╔══╝██╔════╝
// █████╗  ██║   ██║█████╗  ██╔██╗ ██║   ██║   ███████╗
// ██╔══╝  ╚██╗ ██╔╝██╔══╝  ██║╚██╗██║   ██║   ╚════██║
// ███████╗ ╚████╔╝ ███████╗██║ ╚████║   ██║   ███████║
// ╚══════╝  ╚═══╝  ╚══════╝╚═╝  ╚═══╝   ╚═╝   ╚══════╝

export const resize = ({
    map,
    draw,
    isMapLoaded,
    onResize,
    onRelease,
}: {
    map: mapboxgl.Map | null;
    draw: MapboxDraw | null;
    isMapLoaded: boolean;
    onResize: (i: {
        isResizing: boolean;
        isMidpoint: boolean;
        feature?: GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>;
        cursor?: { x: number; y: number };
        canvas: HTMLCanvasElement | undefined;
    }) => void;
    onRelease?: (i: {
        isResizing: boolean;
        isMidpoint: boolean;
        feature?: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
    }) => void;
}): { cleanup: () => void } => {
    let isResizing = false;
    let featureId: string | undefined = undefined;
    let isMidpoint = false;

    const canvas = map?.getCanvas();
    const mapBounds = canvas?.getBoundingClientRect();
    const top = mapBounds ? mapBounds.top : 0;
    const left = mapBounds ? mapBounds.left : 0;

    const onMouseDown = (
        e:
            | (mapboxgl.MapMouseEvent & mapboxgl.EventData)
            | (mapboxgl.MapTouchEvent & mapboxgl.EventData)
    ) => {
        const selectedFeature = draw?.getSelected().features[0];

        const id =
            selectedFeature && isString(selectedFeature.id)
                ? selectedFeature.id
                : undefined;

        if (map && selectedFeature) {
            const features = map.queryRenderedFeatures(e.point, {
                layers: vertexLayers,
            });

            const isVertex =
                !isEmpty(features) &&
                some(
                    features,
                    (f) =>
                        f.properties?.meta === "vertex" ||
                        f.properties?.meta === "midpoint"
                );

            if (isVertex) {
                isResizing = true;
                featureId = id;
                isMidpoint = some(
                    features,
                    (f) => f.properties?.real_meta === "midpoint"
                );
            } else {
                isResizing = false;
                featureId = undefined;
            }
        }
    };

    const onMouseUp = () => {
        if (isEqual(isResizing, true)) {
            onRelease?.({
                isResizing,
                feature: draw && featureId ? draw.get(featureId) : undefined,
                isMidpoint,
            });

            isResizing = false;
            isMidpoint = false;
            featureId = undefined;
            draw?.changeMode("simple_select");
            if (canvas) canvas.style.cursor = "";
        }
    };

    const onDrawUpdate = () => {
        if (isEqual(isResizing, true)) {
            onRelease?.({
                isResizing,
                feature: draw && featureId ? draw.get(featureId) : undefined,
                isMidpoint,
            });
        }

        isResizing = false;
        isMidpoint = false;
        featureId = undefined;
        draw?.changeMode("simple_select");
        if (canvas) canvas.style.cursor = "";
    };

    const onMouseMove = (
        e:
            | (mapboxgl.MapMouseEvent & mapboxgl.EventData)
            | (mapboxgl.MapTouchEvent & mapboxgl.EventData)
    ) => {
        if (isEqual(isResizing, true)) {
            const selectedFeatureWithUpdatedCoordinates =
                featureId && draw ? draw.get(featureId) : undefined;

            const x = isMapTouchEvent(e)
                ? e.originalEvent.touches[0].clientX - left
                : e.originalEvent.clientX - left;
            const y = isMapTouchEvent(e)
                ? e.originalEvent.touches[0].clientY - top
                : e.originalEvent.clientY - top;

            featureId = selectedFeatureWithUpdatedCoordinates?.id?.toString();

            const cursor = { x, y };

            onResize({
                isResizing,
                feature:
                    selectedFeatureWithUpdatedCoordinates as GeoJSON.Feature<
                        GeoJSON.Polygon,
                        GeoJSON.GeoJsonProperties
                    >,
                cursor,
                canvas,
                isMidpoint,
            });
        }
    };

    const onTouchStart = (e: mapboxgl.MapTouchEvent & mapboxgl.EventData) => {
        onMouseDown(e);
    };

    const onTouchMove = (e: mapboxgl.MapTouchEvent & mapboxgl.EventData) => {
        onMouseMove(e);
    };

    const onTouchEnd = () => {
        onMouseUp();
    };

    if (map && isMapLoaded) {
        map.on("mousedown", onMouseDown);
        map.on("mouseup", onMouseUp);
        map.on("mousemove", onMouseMove);
        map.on("draw.update", onDrawUpdate);

        map.on("touchstart", onTouchStart);
        map.on("touchmove", onTouchMove);
        map.on("touchend", onTouchEnd);
    }

    return {
        cleanup: () => {
            if (map) {
                map.off("mousedown", onMouseDown);
                map.off("mouseup", onMouseUp);
                map.off("mousemove", onMouseMove);
                map.off("draw.update", onDrawUpdate);

                map.off("touchstart", onTouchStart);
                map.off("touchmove", onTouchMove);
                map.off("touchend", onTouchEnd);
            }
        },
    };
};

export const drag = ({
    map,
    draw,
    isMapLoaded,
    onDrag,
    mode,
    onRelease,
}: {
    map: mapboxgl.Map | null;
    draw: MapboxDraw | null;
    isMapLoaded: boolean;
    mode: boolean;
    onDrag: (i: {
        isDragging: boolean;
        feature?: GeoJSON.Feature<GeoJSON.Polygon, GeoJSON.GeoJsonProperties>;
        cursor?: { x: number; y: number };
        canvas: HTMLCanvasElement | undefined;
    }) => void;
    onRelease: (i: {
        isDragging: boolean;
        feature?: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
    }) => void;
}): { cleanup: () => void } => {
    let isDragging = false;
    let selected:
        | GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
        | undefined = undefined;

    const canvas = map?.getCanvas();
    const mapBounds = canvas?.getBoundingClientRect();
    const top = mapBounds ? mapBounds.top : 0;
    const left = mapBounds ? mapBounds.left : 0;

    const onClick = (
        e:
            | (mapboxgl.MapMouseEvent & mapboxgl.EventData)
            | (mapboxgl.MapTouchEvent & mapboxgl.EventData)
    ) => {
        const shape = isMapTouchEvent(e)
            ? draw?.getSelected().features[0]
            : e.features[0];

        if (map && shape) {
            selected = {
                type: "Feature",
                id: shape.id,
                geometry: shape.geometry,
                properties: shape.properties,
            };
        }
    };

    const onMouseEnter = (
        e: mapboxgl.MapMouseEvent & {
            features?: mapboxgl.MapboxGeoJSONFeature[] | undefined;
        } & mapboxgl.EventData
    ) => {
        e.preventDefault();
        if (canvas) {
            canvas.style.cursor = "pointer";
        }
    };

    const onMouseUp = () => {
        onRelease({
            isDragging,
            feature: selected,
        });

        if (isEqual(isDragging, true)) {
            isDragging = false;
            draw?.changeMode("simple_select");
            if (canvas) canvas.style.cursor = "";
        }
    };

    const onDrawUpdate = () => {
        if (isEqual(isDragging, true)) {
            onRelease({
                isDragging,
                feature: selected,
            });
        }

        isDragging = false;
        draw?.changeMode("simple_select");
        if (canvas) canvas.style.cursor = "";
    };

    const onMouseMove = (
        e: (mapboxgl.MapMouseEvent & mapboxgl.EventData) | TouchEvent
    ) => {
        const selectedFeatureWithUpdatedCoordinates =
            selected && isString(selected.id) && draw
                ? draw.get(selected.id)
                : undefined;

        const hasMoved = !isEqual(
            selected?.geometry,
            selectedFeatureWithUpdatedCoordinates?.geometry
        );

        const x = isTouchEvent(e)
            ? e.touches[0].clientX - left
            : e.originalEvent.clientX - left;
        const y = isTouchEvent(e)
            ? e.touches[0].clientY - top
            : e.originalEvent.clientY - top;

        const cursor = { x, y };

        if (hasMoved && isEqual(mode, true)) {
            isDragging = true;

            selected = selectedFeatureWithUpdatedCoordinates;

            onDrag({
                isDragging,
                feature:
                    selectedFeatureWithUpdatedCoordinates as GeoJSON.Feature<
                        GeoJSON.Polygon,
                        GeoJSON.GeoJsonProperties
                    >,
                cursor,
                canvas,
            });
        } else {
            isDragging = false;
        }
    };

    const onTouchStart = (e: mapboxgl.MapTouchEvent & mapboxgl.EventData) => {
        onClick(e);
    };

    const onTouchMove = (e: TouchEvent) => {
        onMouseMove(e);
    };

    const onTouchEnd = () => {
        onMouseUp();
    };

    if (map && isMapLoaded && isEqual(mode, true)) {
        map.on("click", `map-shapes-fill-${CustomShapeSource.select}`, onClick);
        map.on(
            "mouseenter",
            `map-shapes-fill-${CustomShapeSource.select}`,
            onMouseEnter
        );
        map.on("mousemove", onMouseMove);
        map.on("mouseup", onMouseUp);
        map.on("draw.update", onDrawUpdate);

        map.on("touchstart", onTouchStart);
        map.on("touchend", onTouchEnd);
        canvas?.addEventListener("touchmove", onTouchMove);
    }

    return {
        cleanup: () => {
            if (map) {
                map.off(
                    "click",
                    `map-shapes-fill-${CustomShapeSource.select}`,
                    onClick
                );
                map.off(
                    "mouseenter",
                    `map-shapes-fill-${CustomShapeSource.select}`,
                    onMouseEnter
                );
                map.off("mousemove", onMouseMove);
                map.off("mouseup", onMouseUp);
                map.off("draw.update", onDrawUpdate);

                map.off("touchstart", onTouchStart);
                map.off("touchend", onTouchEnd);
                canvas?.removeEventListener("touchmove", onTouchMove);
            }
        },
    };
};
